import { useState } from 'react'

import {
  useBatch,
  useRecipe,
  useRecordConsumableReplenishment,
  useRecordReagentReplenishment,
} from '@ctcells/api'
import type { ModuleSnapshot } from '@ctcells/contract'
import { moduleTypeLabel, serverText, t } from '@ctcells/i18n'

import { Button } from '../atoms/Button.tsx'
import type { ActionGate, ScreenAccess } from '../screens/access.ts'
import { QUICK_SUPPLY_TARGETS, recipeSlots } from '../screens/batches/devQuickBatch.ts'
import { SupplyReplenishDialog } from '../screens/monitoring/SupplyReplenishDialog.tsx'
import type { SupplyFixKind } from './preflightFixes.ts'
import type { ReagentBody } from './reagentConfirm.ts'
import { StripReagentDialog } from './StripReagentDialog.tsx'

/**
 * 사전 점검의 소모품 줄 — 링크(「소모품 판으로 →」) 대신 **시작 창 위에서 바로 보충 창**을 띄운다
 * (eric 2026-10-23 「화면 어디 들어가서 버튼 누르게 하지 말고」 · 팀장 2026-10-23 「소모품은 SupplyReplenishDialog 를 그 자리에서」).
 *
 * | 줄 | 창 | 창구 |
 * | --- | --- | --- |
 * | 팁 · TC 부족(`TIP_SHORTAGE_AT_START` · `TIP_STOCK_LOW` · `TC_SHORTAGE_AT_START`) | 모니터링과 같은 「넣었습니까?」(`SupplyReplenishDialog`) | `POST /modules/{id}/consumables/replenish` |
 * | 시약 여유 없음(`REAGENT_VOLUME_LOW`) | 작업 줄과 같은 시약 용량 확인(`StripReagentDialog`) | `IFC-RO-32` |
 *
 * - 🔴 무엇을 채우는지는 **그 줄의 모듈 종류**가 정한다(염색기 = 팁 · 어셈블러 = TC — `QUICK_SUPPLY_TARGETS`). 모듈을 모르면 단추가 없다
 * - 🔴 만재 수는 이 작업 레시피가 선언한 칸 수(`recipeSlots`) — 못 읽으면 서버 표의 기본값(TC)뿐이고, 그것도 없으면 창을 세우지 않는다
 * - 🔴 보충은 «사람이 실제로 채웠다»는 기록이다 — 창이 한 번 묻는다(모니터링과 같은 규칙)
 * - 훅은 단추를 누른 뒤에만 선다(`SupplyHost`) — 창을 열지 않으면 레시피 · 작업 조회도 없다
 */
export interface SupplyFixProps {
  kind: SupplyFixKind
  module: ModuleSnapshot | undefined
  batchId: string
  access: ScreenAccess
  /** 권한 · 끊김 · 진행 중 — 작업 줄 창이 짓는다(보충은 제어락 비대상) */
  writeGate: ActionGate
  /** 보낸 것을 넘긴다 — 창이 그 줄에 진행 · 결과를 찍고 받았으면 점검을 다시 돈다 */
  onRun: (send: () => Promise<unknown>) => void
}

export function SupplyFix(props: SupplyFixProps) {
  const [open, setOpen] = useState(false)
  const label = t(`pfFix.supply.${props.kind}`)
  return (
    <>
      <Button onClick={() => setOpen(true)} size="pill" {...props.writeGate}>
        {label}
      </Button>
      {open ? <SupplyHost {...props} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function SupplyHost({
  kind,
  module,
  batchId,
  access,
  writeGate,
  onRun,
  onClose,
}: SupplyFixProps & { onClose: () => void }) {
  const batch = useBatch({ batchId })
  const recipe = useRecipe({ recipeId: kind === 'reagent' ? undefined : batch.data?.recipe_id })
  const replenish = useRecordConsumableReplenishment()
  const replenishReagent = useRecordReagentReplenishment()
  const [reagentError, setReagentError] = useState<string | undefined>(undefined)

  if (kind === 'reagent') {
    if (batch.data === undefined) return null
    const reagentModule = batch.data.reagent_module_id ?? null
    return (
      <StripReagentDialog
        access={access}
        basis={batch.data.reagent_basis}
        batchId={batchId}
        {...(reagentError === undefined ? {} : { errorMessage: reagentError })}
        moduleId={reagentModule}
        onCancel={onClose}
        onConfirm={(bodies: ReagentBody[]) => {
          if (reagentModule === null) return
          setReagentError(undefined)
          onClose()
          onRun(async () => {
            for (const body of bodies)
              await replenishReagent.mutateAsync({
                moduleId: reagentModule,
                labwareId: body.labwareId,
                wellNames: body.wellNames,
                volume: body.volume,
                note: serverText('strip.reagentNote', { batch: batchId }),
              })
          })
        }}
        pending={replenishReagent.isPending}
      />
    )
  }

  const target = QUICK_SUPPLY_TARGETS.find((one) => one.kind === kind)
  if (module === undefined || target === undefined) return null
  if (batch.data === undefined || (recipe.data === undefined && !recipe.isError)) return null
  /* 🔴 칸 수를 모르면 지어내지 않는다 — 창은 서되 「예」가 비활성 + 사유다 */
  const full = recipeSlots(recipe.data?.payload, target.labwareType) ?? target.fallbackSlots
  const bag: Readonly<Record<string, unknown>> = module.consumables ?? {}
  const raw = bag[target.column]
  const disabledReason =
    full === undefined
      ? t('pfFix.supply.noCapacity')
      : writeGate.disabled
        ? writeGate.disabledReason
        : undefined
  return (
    <SupplyReplenishDialog
      onClose={onClose}
      view={{
        kind,
        moduleName: moduleTypeLabel(module.module_type) ?? module.module_id,
        full: full ?? 0,
        ...(typeof raw === 'number' ? { current: raw } : {}),
        ...(disabledReason === undefined ? {} : { disabledReason }),
        onSubmit: ({ count, note }) => {
          const sent = replenish.mutateAsync({
            moduleId: module.module_id,
            kind,
            count,
            ...(note === undefined ? {} : { note }),
          })
          onRun(() => sent)
          return sent
        },
      }}
    />
  )
}
